import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setRemitterData } from "../../../Store/Slices/AuthSlice";
import {
  CheckCircle,
  AlertCircle,
  CreditCard,
  Loader2,
  Shield,
  XCircle,
} from "lucide-react";
import useRDService from "../../../CustomHooks/useRDService";
import $ from "jquery";

// Inline Input component
const Input = ({ label, name, value, onChange, disabled, type = "text" }) => (
  <div>
    <label className="block mb-1 text-sm font-medium text-slate-700">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`w-full border rounded-lg p-3 ${
        disabled ? "bg-slate-100" : ""
      }`}
    />
  </div>
);

const Dmt = () => {
  // --- TAB STATE ---
  const [activeTab, setActiveTab] = useState("DMT"); // 'DMT' or 'KYC'

  // --- DMT STATES (unchanged) ---
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [impsType, setImpsType] = useState("IMPS2");
  // const [impsType, setImpsType] = useState("IMPS1");
  const [publicIp, setPublicIp] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(null);
  const [isFormDisabled, setIsFormDisabled] = useState(false);
  const [navigateBeni, setNavigateBeni] = useState(false);
  const [navigateRegister, setNavigateRegister] = useState(false);
  const [instRef, setInstRef] = useState("");
  const [availableLimit, setAvailableLimit] = useState(0);
  const [consumeLimit, setConsumeLimit] = useState(0);
  const [limitTotal, setLimitTotal] = useState(0);
  const [blockVerify, setBlockVerify] = useState(false);

  // --- KYC STATES (from EkycComponent) ---
  const [loading, setLoading] = useState(false);
  const [rdLoading, setRdLoading] = useState(false);
  const [biometricCompleted, setBiometricCompleted] = useState(false);
  const [showCapture, setShowCapture] = useState(false);
  const [bioData, setBioData] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [pidOptionWadh, setPidOptionWadh] = useState("");
  const [biometricStatusData, setBiometricStatusData] = useState({
    status: "",
    action: "",
    kycStatus: "",
  });

  // KYC form fields (pre-filled from merchantdetails if available)
  const merchantdetails = useSelector((state) => state.auth?.employeeDetails);
  const [form, setForm] = useState({
    name: merchantdetails?.name || "",
    mobile: merchantdetails?.mobile_number || "",
    email: merchantdetails?.email || "",
    pan: merchantdetails?.pan || "",
    aadhaar: merchantdetails?.aadhaar_number || "",
    dob: merchantdetails?.dob || "",
    gender: merchantdetails?.gender || "",
    address: merchantdetails?.address?.full || "",
    city: merchantdetails?.address?.city || "",
    pincode: merchantdetails?.address?.pincode || "",
  });

  // --- Hooks ---
  const { callApi: verifyCustomer } = useApi("/dmt/verifyCustomer", "POST");
  const { callApi: fetchBiometricStatus } = useApi("/aeps/biometric/status", "POST");
  const { callApi: initiateMinKyc } = useApi("/aeps/signup/min-kyc", "POST");
  const { callApi: submitBiometric } = useApi("/aeps/biometric/submit", "POST");
  const { captureFingerprint, loading: rdLoadingHook } = useRDService();

  const details = useSelector((store) => store?.auth?.employeeDetails);
  const { lat, lng } = useSelector((s) => s.location);
  const latlong = lat && lng ? `${lat}_${lng}` : "";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Keep rdLoading in sync with hook
  useEffect(() => {
    setRdLoading(rdLoadingHook);
  }, [rdLoadingHook]);

  // --- IP fetch (DMT) ---
  useEffect(() => {
    fetch("https://api64.ipify.org?format=json")
      .then((res) => res.json())
      .then((data) => setPublicIp(data.ip))
      .catch((error) => console.error("Error fetching IP:", error));
  }, []);

  // --- Fetch Biometric Status (KYC) ---
  useEffect(() => {
    const getBiometricStatus = async () => {
      try {
        setLoading(true);
        const res = await fetchBiometricStatus({
          merchantId: merchantdetails?.username,
          serviceType: "DMI", // as per original
        });

        if (
          Boolean(res?.outletId) &&
          res?.action?.toUpperCase() === "ACTION-REQUIRED" &&
          res?.kycStatus?.toUpperCase() === "PENDING"
        ) {
          setShowCapture(true);
          setBioData({
            merchantId: res?.merchantId,
            referenceKey: res?.referenceKey,
            pidOptionWadh: res?.pidOptionWadh,
            outletId: res?.outletId,
          });
        } else if (
          res?.statuscode === "TXN" &&
          res?.action?.toUpperCase() === "NO-ACTION-REQUIRED"
        ) {
          setBiometricCompleted(true);
          setBiometricStatusData({
            status: res?.status,
            action: res?.action,
            kycStatus: res?.kycStatus,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (merchantdetails?.username) {
      getBiometricStatus();
    }
  }, [merchantdetails?.username]);

  // --- DMT Helper functions ---
  const goToRemittance = ({
    name,
    mobile,
    publicIp,
    latlong,
    instRef,
    impsType,
    availableLimit = 0,
    consumeLimit = 0,
    limitTotal = 0,
  }) => {
    navigate("/remittance", {
      state: {
        name,
        mobile,
        publicIp,
        latlong,
        instRef,
        impsType,
        availableLimit,
        consumeLimit,
        limitTotal,
      },
    });
  };

  const handleSubmit = async () => {
    setMessage("");
    setMessageType("");
    setIsLoading(true);

    if (!mobile || !impsType) {
      setMessage("Please fill all fields before proceeding.");
      setMessageType("error");
      setIsLoading(false);
      return;
    }

    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setMessage("Mobile number must start with a digit between 6 and 9 and be 10-digit.");
      setMessageType("error");
      setIsLoading(false);
      return;
    }

    const isSequential = (str) => {
      str = str.toLowerCase().replace(/ /g, "");
      for (let i = 0; i < str.length - 2; i++) {
        const a = str.charCodeAt(i);
        const b = str.charCodeAt(i + 1);
        const c = str.charCodeAt(i + 2);
        if (b === a + 1 && c === b + 1) return true;
      }
      return false;
    };

    if (name && isSequential(name)) {
      setMessage("Name should not contain sequential characters like 'abc'.");
      setMessageType("error");
      setIsLoading(false);
      return;
    }

    if (latlong === "") {
      setMessage("Please enable location");
      setMessageType("error");
      setIsLoading(false);
      return;
    }

    if (publicIp === "") {
      setMessage("Unable to fetch public IP. Please try again later.");
      setMessageType("error");
      setIsLoading(false);
      return;
    }

    if (impsType === "IMPS2" && !details?.outlet_id) {
      setMessage("Please complete Merchant eKYC to use IMPS2 DMT service.");
      setMessageType("error");
      setIsLoading(false);
      // setTimeout(() => navigate("/merchantekyc"), 1500);
      return;
    }

    try {
      const requestBody = {
        mobile_number: mobile,
        name: name,
        imps_type: impsType,
        merchant_id: details?.userId,
        username: details?.username,
        latlong: latlong,
        public_ip: publicIp,
      };

      const response = await verifyCustomer(requestBody);

      if (
        impsType === "IMPS1" &&
        response?.errorCode === "EZYPAY_CUSTOMER_REGISTRATION_FAILED"
      ) {
        setMessage(response.message);
        setMessageType("error");
        setBlockVerify(true);
        setIsLoading(false);
        return;
      }

      if (response?.type === "IMPS2") {
        dispatch(setRemitterData(response));
        setName(response?.remitter_name || name);
        setIsVerified(response?.is_verified);
        setIsFormDisabled(true);
        setLimitTotal(response?.remitter_data?.limitTotal);
        setAvailableLimit(response?.remitter_data?.limitAvailable);
        setConsumeLimit(response?.remitter_data?.limitConsumed);
        setImpsType("IMPS2");
        setInstRef(response?.reference_key);
         setPidOptionWadh(response?.pidOptionWadh || response?.remitter_data?.pidOptionWadh || "");
        if (
          response?.is_verified === true &&
          response?.remitter_data?.kyctypeFlag === true
        ) {
          goToRemittance({
            name: response?.remitter_name || name,
            mobile,
            publicIp,
            latlong,
            instRef: response?.reference_key,
            impsType: "IMPS2",
            availableLimit: response?.remitter_data?.limitAvailable,
            consumeLimit: response?.remitter_data?.limitConsumed,
            limitTotal: response?.remitter_data?.limitTotal,
          });
        } else {
          setNavigateRegister(true);
        }
      } else if (response?.type === "IMPS") {
        dispatch(setRemitterData({ ...response, type: "IMPS1" }));
        setName(response?.remitter_name || name);
        setInstRef(response?.reference_key);
        setIsVerified(response?.is_verified);
        setIsFormDisabled(true);
        setImpsType("IMPS1");

        if (response?.customer_verification_message) {
          setMessage(response.customer_verification_message);
          setMessageType("success");
        }

        if (response?.is_verified === true) {
          goToRemittance({
            name: response?.remitter_name || name,
            mobile,
            publicIp,
            latlong,
            instRef: response?.reference_key,
            impsType: "IMPS1",
          });
        } else {
          setNavigateRegister(true);
        }
      } else if (response?.error || response?.status === 202) {
        setMessage(response?.message || "Verification failed");
        setMessageType("error");
        setBlockVerify(true);
      }
    } catch (error) {
      setMessage(
        error?.response?.data?.message || "Something went wrong. Please try again."
      );
      setMessageType("error");
      setBlockVerify(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlenavigateBeni = () => {
    navigate("/remittance", {
      state: {
        name,
        mobile,
        publicIp,
        latlong,
        instRef,
        impsType,
        availableLimit,
        consumeLimit,
        limitTotal,
      },
    });
  };

  const handlenavigateRegister = () => {
    navigate("/registercustomers", {
      state: {
        name,
        mobile,
        publicIp,
        latlong,
        instRef,
        impsType,
        merchantId: details?.userId,
        wadh: pidOptionWadh,
      },
    });
  };

  const resetForm = () => {
    setName("");
    setMobile("");
    setImpsType("");
    setMessage("");
    setMessageType("");
    setIsVerified(null);
    setIsFormDisabled(false);
    setNavigateRegister(false);
    setNavigateBeni(false);
    setBlockVerify(false);
  };

  const impsOptions = [{ label: "Bank1", value: "IMPS2" }];

  // --- KYC Handlers (from EkycComponent) ---
  const handleKycChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const formatDOB = (dob) => {
    if (!dob) return "";
    const d = new Date(dob);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;
  };

  const parsePidXml = (xml) => {
    const $xml = $(xml);
    const get = (selector, attr) => $xml.find(selector).attr(attr) || "0";
    const infoValue = (name) =>
      $xml.find(`Info[name='${name}']`).attr("value") ||
      $xml.find(`Param[name='${name}']`).attr("value") ||
      "0";

    return {
      dc: get("DeviceInfo", "dc"),
      ci: get("Skey", "ci"),
      hmac: $xml.find("Hmac").text() || "0",
      dpId: get("DeviceInfo", "dpId"),
      mc: get("DeviceInfo", "mc"),
      pidDataType: get("Data", "type"),
      sessionKey: $xml.find("Skey").text() || "0",
      mi: get("DeviceInfo", "mi"),
      rdsId: get("DeviceInfo", "rdsId"),
      errCode: get("Resp", "errCode"),
      errInfo: get("Resp", "errInfo"),
      fCount: get("Resp", "fCount"),
      fType: get("Resp", "fType"),
      iCount: get("Resp", "iCount"),
      iType: get("Resp", "iType"),
      pCount: get("Resp", "pCount"),
      pType: "0",
      srno: infoValue("srno"),
      sysid: infoValue("sysid"),
      ts: infoValue("ts"),
      pidData: $xml.find("Data").text() || "0",
      qScore: get("Resp", "qScore"),
      nmPoints: get("Resp", "nmPoints"),
      rdsVer: get("DeviceInfo", "rdsVer"),
    };
  };

  const handleEkyc = async () => {
    try {
      setLoading(true);
      const body = {
        mobile: form.mobile,
        name: form.name,
        gender: form.gender,
        pan: form.pan.toUpperCase(),
        email: form.email,
        aadhaar: form.aadhaar,
        dateOfBirth: formatDOB(form.dob),
        latitude: lat?.toString(),
        longitude: lng?.toString(),
        address: {
          full: form.address,
          city: form.city,
          pincode: form.pincode,
        },
      };

      const res = await initiateMinKyc(body, `?userId=${details?.userId}`);

      if (res?.statuscode === "TXN") {
        setMessage("Min KYC completed. Capture biometric.");
        setBioData({
          merchantId: merchantdetails?.username,
          referenceKey: res?.ipayUuid || "",
          pidOptionWadh: "",
          outletId: res?.outletId || "",
        });
        setShowCapture(true);
        return;
      }

      setMessage(res?.message || res?.status || "E-KYC initiation failed");
    } catch {
      setMessage("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricSubmit = async () => {
    try {
      setLoading(true);
      setMessage("");

      const pidXml = await captureFingerprint(bioData?.pidOptionWadh || "");
      const payload = parsePidXml(pidXml);

      const res = await submitBiometric({
        merchantId: bioData?.merchantId,
        referenceKey: bioData?.referenceKey,
        latitude: lat?.toString(),
        longitude: lng?.toString(),
        biometricData: payload,
      });

      if (
        res?.statuscode === "TXN" &&
        res?.kycStatus === "SUCCESS" &&
        res?.isKycCompleted === true &&
        res?.status?.includes("Biometric KYC completed")
      ) {
        setMessage("Biometric KYC completed successfully");
        setBiometricCompleted(true);
        // Switch back to DMT tab after success
        setTimeout(() => {
          setActiveTab("DMT");
        }, 1500);
        return;
      }

      setMessage(res?.status || res?.message || "Biometric verification failed");
    } catch (error) {
      setMessage(error?.response?.data?.status || error?.message || "Biometric failed");
    } finally {
      setLoading(false);
    }
  };

  // Disable DMT form if KYC is not complete
  const isDmtDisabled = !biometricCompleted;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-cyan-50 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Tab Toggle */}
        <div className="flex bg-slate-200/50 p-1.5 rounded-2xl max-w-md mx-auto shadow-sm border border-slate-200 backdrop-blur-sm">
          <button
            onClick={() => setActiveTab("DMT")}
            className={`flex-1 py-3 text-sm font-semibold rounded-xl transition-all duration-300 ${
              activeTab === "DMT"
                ? "bg-white text-indigo-700 shadow-md shadow-slate-200/50"
                : "text-slate-500 hover:text-slate-700 hover:bg-slate-200"
            }`}
          >
            DMT Transfer
          </button>
          <button
            onClick={() => setActiveTab("KYC")}
            className={`flex-1 py-3 text-sm font-semibold rounded-xl transition-all duration-300 ${
              activeTab === "KYC"
                ? "bg-white text-indigo-700 shadow-md shadow-slate-200/50"
                : "text-slate-500 hover:text-slate-700 hover:bg-slate-200"
            }`}
          >
            E-KYC Setup
          </button>
        </div>

        {/* DMT TAB */}
        {activeTab === "DMT" && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-cyan-500 flex items-center justify-center text-white shadow-lg">
                <CreditCard className="w-7 h-7" />
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-slate-900">
                Domestic Money Transfer
              </h1>
              <p className="mt-1 text-slate-600">
                Verify customer and proceed with secure IMPS transfers
              </p>
            </div>

            <div className="bg-white/90 backdrop-blur-xl rounded-3xl border border-slate-200 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.35)] p-8">
              {!biometricCompleted && !loading && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 flex gap-3 mb-6 items-start">
                  <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-sm">Biometric KYC Required</h4>
                    <p className="text-sm mt-1 opacity-90">
                      You must complete your E-KYC verification before making DMT
                      transfers. Please switch to the E-KYC tab above.
                    </p>
                  </div>
                </div>
              )}

              {isVerified !== null && (
                <div
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl mb-6 ${
                    isVerified
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-rose-50 text-rose-700 border border-rose-200"
                  }`}
                >
                  {isVerified ? <Shield size={18} /> : <XCircle size={18} />}
                  <span className="font-semibold text-sm">
                    {isVerified ? "Customer Verified" : "Customer Not Verified"}
                  </span>
                </div>
              )}

              <div
                className={`space-y-5 transition-opacity ${
                  isDmtDisabled ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                <p className="text-xs uppercase tracking-widest text-slate-500">
                  Step 1 · Customer Details
                </p>

                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Mobile Number
                  </label>
                  <input
                    value={mobile}
                    maxLength={10}
                    onChange={(e) =>
                      /^\d{0,10}$/.test(e.target.value) && setMobile(e.target.value)
                    }
                    disabled={isFormDisabled || isDmtDisabled}
                    placeholder="10-digit mobile number"
                    className={`mt-1 w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-indigo-600 focus:outline-none ${
                      isFormDisabled || isDmtDisabled ? "bg-slate-100" : ""
                    }`}
                  />
                </div>

                <div className="mt-8 space-y-4 hidden">
                  <p className="text-xs uppercase tracking-widest text-slate-500">
                    Select IMPS Pipe
                  </p>
                  <div className="grid grid-cols-1 gap-4">
                    {impsOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={isFormDisabled || isDmtDisabled}
                        onClick={() => setImpsType(opt.value)}
                        className={`p-4 rounded-2xl border text-sm font-semibold transition-all ${
                          impsType === opt.value
                            ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                            : "border-slate-200 hover:border-slate-300"
                        } ${
                          isFormDisabled || isDmtDisabled
                            ? "opacity-60 cursor-not-allowed"
                            : ""
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {message && (
                  <div
                    className={`mt-6 flex items-center gap-2 px-4 py-3 rounded-xl text-sm ${
                      messageType === "success"
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-rose-50 text-rose-700 border border-rose-200"
                    }`}
                  >
                    {messageType === "success" ? (
                      <CheckCircle size={16} />
                    ) : (
                      <AlertCircle size={16} />
                    )}
                    {message}
                  </div>
                )}

                <div className="mt-8 space-y-3">
                  <button
                    onClick={handleSubmit}
                    disabled={
                      isLoading ||
                      !mobile ||
                      !impsType ||
                      isFormDisabled ||
                      blockVerify ||
                      isDmtDisabled
                    }
                    className={`w-full py-3 rounded-xl font-semibold text-white flex items-center justify-center gap-2 ${
                      isLoading ||
                      !mobile ||
                      !impsType ||
                      isFormDisabled ||
                      isDmtDisabled
                        ? "bg-slate-400"
                        : "bg-gradient-to-r from-indigo-600 to-cyan-500 hover:shadow-lg"
                    }`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Verifying…
                      </>
                    ) : (
                      "Verify Customer"
                    )}
                  </button>

                  {isFormDisabled && (
                    <button
                      onClick={resetForm}
                      disabled={isDmtDisabled}
                      className="w-full py-3 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50"
                    >
                      Start New Verification
                    </button>
                  )}

                  {navigateBeni && (
                    <button
                      onClick={handlenavigateBeni}
                      disabled={isDmtDisabled}
                      className="w-full py-3 rounded-xl border border-indigo-600 text-indigo-700 font-semibold hover:bg-indigo-50"
                    >
                      Proceed to Remittance
                    </button>
                  )}

                  {navigateRegister && (
                    <button
                      onClick={handlenavigateRegister}
                      disabled={isDmtDisabled}
                      className="w-full py-3 rounded-xl border border-indigo-600 text-indigo-700 font-semibold hover:bg-indigo-50"
                    >
                      Register Customer
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* KYC TAB (fully integrated from EkycComponent) */}
        {activeTab === "KYC" && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="overflow-hidden p-4 flex items-center">
              <div className="w-full bg-white rounded-3xl border border-slate-200 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.35)] p-8">
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                  <button
                    onClick={() => setActiveTab("DMT")}
                    className="px-4 py-2 bg-gray-100 rounded-lg font-medium text-slate-700 hover:bg-gray-200 transition"
                  >
                    ← Back to DMT
                  </button>
                  {!showCapture && !biometricCompleted && (
                    <button
                      onClick={() => setEditMode(!editMode)}
                      className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 transition text-white font-medium rounded-lg"
                    >
                      {editMode ? "Save Details" : "Edit Details"}
                    </button>
                  )}
                </div>

                <h2 className="text-2xl font-semibold mb-6 text-slate-900">
                  E-KYC Verification
                </h2>

                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
                    <p className="mt-4 text-slate-600">Checking E-KYC Status...</p>
                  </div>
                ) : biometricCompleted ? (
                  <div className="text-center py-10">
                    <h3 className="text-2xl text-green-600 font-medium mb-6">
                      ✅ {biometricStatusData.status}
                    </h3>
                    <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-left inline-block w-full max-w-md">
                      <p className="text-slate-800 mb-2">
                        <b>Action:</b> {biometricStatusData.action}
                      </p>
                      <p className="text-slate-800">
                        <b>KYC Status:</b> {biometricStatusData.kycStatus}
                      </p>
                    </div>
                  </div>
                ) : showCapture ? (
                  <div className="text-center py-12">
                    <h3 className="text-xl font-medium text-slate-800 mb-6">
                      Biometric Authentication Required
                    </h3>
                    <button
                      onClick={handleBiometricSubmit}
                      disabled={loading || rdLoading}
                      className={`w-full max-w-sm mx-auto py-4 font-semibold text-white rounded-xl transition ${
                        loading || rdLoading
                          ? "bg-slate-400"
                          : "bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-200"
                      }`}
                    >
                      {loading || rdLoading ? "Capturing..." : "Capture Fingerprint"}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mb-8">
                      <h3 className="font-semibold text-slate-800 mb-4 border-b pb-2">
                        Personal Details
                      </h3>
                      <div className="grid md:grid-cols-2 gap-5">
                        <Input
                          label="Full Name"
                          name="name"
                          value={form.name}
                          onChange={handleKycChange}
                          disabled={!editMode}
                        />
                        <Input
                          label="Mobile"
                          name="mobile"
                          value={form.mobile}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, "");
                            if (val.length <= 10)
                              handleKycChange({ target: { name: "mobile", value: val } });
                          }}
                          disabled={!editMode}
                        />
                        <Input
                          label="Email"
                          name="email"
                          value={form.email}
                          onChange={handleKycChange}
                          disabled={!editMode}
                        />
                        <Input
                          label="PAN"
                          name="pan"
                          value={form.pan}
                          disabled={true}
                        />
                        <Input
                          label="Aadhaar"
                          name="aadhaar"
                          value={form.aadhaar}
                          disabled={true}
                        />
                        <Input
                          label="DOB"
                          type="date"
                          name="dob"
                          value={form.dob}
                          onChange={handleKycChange}
                          disabled={!editMode}
                        />
                        <div>
                          <label className="block mb-1 text-sm font-medium text-slate-700">
                            Gender
                          </label>
                          <select
                            name="gender"
                            value={form.gender}
                            onChange={handleKycChange}
                            disabled={!editMode}
                            className={`w-full border rounded-lg p-3 ${
                              !editMode ? "bg-slate-100" : ""
                            }`}
                          >
                            <option value="">Select</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                            <option value="T">Transgender</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div className="mb-8">
                      <h3 className="font-semibold text-slate-800 mb-4 border-b pb-2">
                        Address Details
                      </h3>
                      <div className="grid md:grid-cols-3 gap-5">
                        <Input
                          label="Address"
                          name="address"
                          value={form.address}
                          onChange={handleKycChange}
                          disabled={!editMode}
                        />
                        <Input
                          label="City"
                          name="city"
                          value={form.city}
                          onChange={handleKycChange}
                          disabled={!editMode}
                        />
                        <Input
                          label="Pincode"
                          name="pincode"
                          value={form.pincode}
                          onChange={handleKycChange}
                          disabled={!editMode}
                        />
                      </div>
                    </div>

                    <button
                      onClick={handleEkyc}
                      disabled={loading}
                      className="w-full py-4 font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition shadow-lg shadow-indigo-200"
                    >
                      {loading ? "Processing..." : "Start E-KYC"}
                    </button>
                  </>
                )}

                {message && activeTab === "KYC" && (
                  <div className="mt-5 p-4 bg-rose-50 text-rose-700 rounded-xl text-center font-medium">
                    {message}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-slate-500 font-medium">
          Secure • Encrypted • RBI-compliant transactions
        </p>
      </div>
    </div>
  );
};

export default Dmt;